import { useCallback, useMemo, useRef, useState } from 'react'
import type { Moment } from 'moment'
import { useIntl } from 'react-intl'

import { DATE_SEARCH_OPTION_IDS, type FLEXIBLE_DURATION_RANGE } from 'constants/search'
import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DMY_CASUAL_SHORT_FORMAT,
} from 'constants/dateFormats'
import {
  addDays,
  addMonths,
  beginningOfToday,
  useIntlDateFormatter,
} from 'lib/datetime/dateUtils'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'

interface UseCarouselDateFilterArgs {
  anytimeLabel: string
}

export default function useCarouselDateFilter({ anytimeLabel }: UseCarouselDateFilterArgs) {
  const intl = useIntl()
  const dateFormatter = useIntlDateFormatter()
  const today = useMemo(() => beginningOfToday(), [])

  const [dateMode, setDateMode] = useState<DATE_SEARCH_OPTION_IDS>(DATE_SEARCH_OPTION_IDS.ANYTIME)
  const [checkIn, setCheckIn] = useState<Moment | undefined>(undefined)
  const [checkOut, setCheckOut] = useState<Moment | undefined>(undefined)
  const [flexibleMonths, setFlexibleMonths] = useState<string>('')
  const [flexibleNights, setFlexibleNights] = useState<FLEXIBLE_DURATION_RANGE | undefined>(undefined)

  const [open, setOpen] = useState(false)
  const [pendingDateMode, setPendingDateMode] = useState<DATE_SEARCH_OPTION_IDS>(DATE_SEARCH_OPTION_IDS.ANYTIME)
  const [pendingCheckIn, setPendingCheckIn] = useState<Moment | undefined>(undefined)
  const [pendingCheckOut, setPendingCheckOut] = useState<Moment | undefined>(undefined)
  const [pendingFlexibleMonths, setPendingFlexibleMonths] = useState<string>('')
  const [pendingFlexibleNights, setPendingFlexibleNights] = useState<FLEXIBLE_DURATION_RANGE | undefined>(undefined)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const chipLabel = useMemo(() => {
    if (dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && checkIn && checkOut) {
      const startFormat = checkIn.year() !== checkOut.year() ?
        INTL_DMY_CASUAL_SHORT_FORMAT :
        INTL_DAY_MONTH_NAME_SHORT
      const endFormat = checkIn.year() !== checkOut.year() ?
        INTL_DMY_CASUAL_SHORT_FORMAT :
        INTL_DAY_MONTH_NAME_SHORT

      return `${dateFormatter(checkIn, startFormat)} - ${dateFormatter(checkOut, endFormat)}`
    }

    if (dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE && (flexibleMonths || flexibleNights)) {
      return prettyFlexibleDates(flexibleMonths, flexibleNights, undefined, intl)
    }

    return anytimeLabel
  }, [anytimeLabel, checkIn, checkOut, dateMode, flexibleMonths, flexibleNights, intl, dateFormatter])

  const filterNights = useMemo<number | undefined>(() => {
    if (dateMode !== DATE_SEARCH_OPTION_IDS.SPECIFIC || !checkIn || !checkOut) return undefined
    const nights = checkOut.diff(checkIn, 'days')
    return nights > 0 ? nights : undefined
  }, [checkIn, checkOut, dateMode])

  const handleToggle = useCallback(() => {
    if (!open) {
      setPendingDateMode(dateMode)
      setPendingCheckIn(checkIn)
      setPendingCheckOut(checkOut)
      setPendingFlexibleMonths(flexibleMonths)
      setPendingFlexibleNights(flexibleNights)
    }
    setOpen(prev => !prev)
  }, [checkIn, checkOut, open, dateMode, flexibleMonths, flexibleNights])

  const handleClose = useCallback(() => {
    setOpen(false)
  }, [])

  const handleSelectSpecific = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.SPECIFIC)
    setPendingFlexibleMonths('')
    setPendingFlexibleNights(undefined)
  }, [])

  const handleSelectFlexible = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
    setPendingCheckIn(undefined)
    setPendingCheckOut(undefined)
  }, [])

  const handleSelectAnytime = useCallback(() => {
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.ANYTIME)
    setPendingCheckIn(undefined)
    setPendingCheckOut(undefined)
    setPendingFlexibleMonths('')
    setPendingFlexibleNights(undefined)
  }, [])

  const handlePendingDatesChange = useCallback((dates: { startDate: Moment; endDate?: Moment | null }) => {
    setPendingCheckIn(dates.startDate)
    setPendingCheckOut(dates.endDate ?? undefined)
    if (dates.startDate && dates.endDate) {
      setPendingDateMode(DATE_SEARCH_OPTION_IDS.SPECIFIC)
    }
  }, [])

  const handlePendingFlexibleNightsChange = useCallback((duration: FLEXIBLE_DURATION_RANGE) => {
    setPendingFlexibleNights(duration)
    setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
  }, [])

  const handlePendingFlexibleMonthsChange = useCallback((months: string) => {
    setPendingFlexibleMonths(months)
    if (months.length > 0) {
      setPendingDateMode(DATE_SEARCH_OPTION_IDS.FLEXIBLE)
    }
  }, [])

  const handleApply = useCallback(() => {
    setDateMode(pendingDateMode)
    setCheckIn(pendingCheckIn)
    setCheckOut(pendingCheckOut)
    setFlexibleMonths(pendingFlexibleMonths)
    setFlexibleNights(pendingFlexibleNights)
    setOpen(false)
  }, [pendingCheckIn, pendingCheckOut, pendingDateMode, pendingFlexibleMonths, pendingFlexibleNights])

  const clear = useCallback(() => {
    setDateMode(DATE_SEARCH_OPTION_IDS.ANYTIME)
    setCheckIn(undefined)
    setCheckOut(undefined)
    setFlexibleMonths('')
    setFlexibleNights(undefined)
  }, [])

  const calendarMaxDate = useMemo(
    () => pendingCheckIn && !pendingCheckOut ? addDays(pendingCheckIn.toDate(), 31) : addMonths(today, 36),
    [pendingCheckIn, pendingCheckOut, today],
  )

  const shouldShowSpecificPicker = pendingDateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC ||
    pendingDateMode === DATE_SEARCH_OPTION_IDS.ANYTIME
  const shouldShowFlexiblePicker = pendingDateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE
  const isApplyDisabled = pendingDateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC &&
    !(pendingCheckIn && pendingCheckOut)

  return {
    dateMode,
    checkIn,
    checkOut,
    flexibleMonths,
    flexibleNights,
    chipLabel,
    filterNights,
    open,
    pendingDateMode,
    pendingCheckIn,
    pendingCheckOut,
    pendingFlexibleMonths,
    pendingFlexibleNights,
    triggerRef,
    today,
    calendarMaxDate,
    shouldShowSpecificPicker,
    shouldShowFlexiblePicker,
    isApplyDisabled,
    handleToggle,
    handleClose,
    handleSelectSpecific,
    handleSelectFlexible,
    handleSelectAnytime,
    handlePendingDatesChange,
    handlePendingFlexibleNightsChange,
    handlePendingFlexibleMonthsChange,
    handleApply,
    clear,
  }
}
